import {
  BasicStep,
  CompanyType,
  ComponentStep,
  DocumentType,
  Field,
  PreconfirmationField,
  ShareholderData,
  CivilStatus,
  PitbullCountries,
  SerializableFile,
  Step,
  OnboardingType,
  PartnerCompanyType,
  DraftUUID,
} from "../types";
import logoAllariaMas from "../assets/images/logos/allariamas.svg";
import logoAllaria from "../assets/images/logos/allaria.svg";
import logoAgroFinanzas from "../assets/images/logos/agrofinanzas.svg";
import logoAllariaSecurities from "../assets/images/logos/allaria-securities.svg";
import logoAllariaAssetManagement from "../assets/images/logos/allaria-asset-management.png";
import faviconAllariaMas from "../assets/favicon.png";
import faviconAllaria from "../assets/faviconAllaria.png";
import { FormikValues } from "formik";
import {
  checkAlreadyRegistered,
  getCountryList,
} from "../connectors/connectors";
import moment from "moment";
import { isDev } from "../app/utils";
import { deleteDoc, doc, Firestore } from "firebase/firestore";
import { InfoStepType, onUpdateInfoStep } from "../reducers/infoStepReducer";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import { app } from "../main";
import { AppDispatch } from "../app/store";
import { onUpdateStep } from "../reducers/onboardingDataReducer";
import * as Sentry from "@sentry/react";

const staticUrl = import.meta.env.VITE_BASE_URL_STATIC;

// Function to check if a Step is a BasicStep
export const isBasicStep = (
  step?: Step | BasicStep | ComponentStep
): step is BasicStep => {
  // Check if step is defined and does not have a component property
  return step !== undefined && ("fields" in step || "subSteps" in step);
};

// Function to check if a Step is a ComponentStep
export const isComponentStep = (
  step?: Step | BasicStep | ComponentStep
): step is ComponentStep => {
  // Check if step is defined and has a component property
  return step !== undefined && "component" in step;
};

export const getOnboardingFields = (steps: Step[]) => {
  const getFieldsByStep = (step: Step) => {
    let fields: Field[] = [];

    if (step.fields) {
      fields = step.fields;
    } else if (step.subSteps) {
      fields = step.subSteps.flatMap(
        (subStep: BasicStep) => subStep.fields ?? []
      );
    }

    return fields;
  };

  const onboardingFields = steps.flatMap((step) => getFieldsByStep(step));
  return onboardingFields;
};

export const getPreconfirmationStepFieldsByName = (fields: Field[]) => {
  const formattedFields = getFormattedPreconfirmationFields(fields);

  const fieldsByName: Record<string, PreconfirmationField> =
    formattedFields.reduce((acc, field) => {
      return { ...acc, [field.name]: field };
    }, {});

  return fieldsByName;
};

export const isUnifiedOnboarding = (): boolean => {
  if (!isDev) return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.has("accountId") &&
    params.has("accountName") &&
    params.has("accountCuit") &&
    params.has("accountEmail")
  );
};

export const getFormattedPreconfirmationFields = (
  fields: Field[],
  values: FormikValues | null = null
) => {
  return fields.map(
    ({
      name,
      label,
      type,
      options,
      conditional,
      preconfirmationLabel,
      formatInPreConfirmation,
      valueGetter,
      canUploadMultipleFiles,
    }) => ({
      name,
      label: preconfirmationLabel ?? label,
      type,
      options,
      isConditional: !!conditional,
      formatInPreConfirmation,
      values,
      valueGetter,
      canUploadMultipleFiles,
    })
  );
};

export const getCompanyData = (formValues?: FormikValues | null) => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  const staticUrl = import.meta.env.VITE_BASE_URL_STATIC;

  switch (company) {
    case "ALLARIA":
      return {
        formTitle: "Onboarding Allaria",
        logo: logoAllaria,
        favicon: faviconAllaria,
        companyName: company,
        termsAndConditions: [
          {
            name: "termsAndConditions",
            required: true,
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
                {" y "}
                <span id="platform-use">Uso de la plataforma</span>
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones de Allaria",
                url: `${staticUrl}/terms/T%26C+Allaria+con+A%2B.pdf`,
              },
              {
                id: "platform-use",
                name: "Uso de la plataforma",
                url: `${staticUrl}/terms/Términos+y+condiciones+de+Uso+de+la+Plataforma.pdf`,
              },
            ],
          },
          ...(formValues && isLandToken(formValues || {})
            ? [
                // Uncomment in V2,
                // {
                //   required: true,
                //   name: "landTokenTermsAndConditions",
                //   label: (
                //     <div>
                //       Acepto los{" "}
                //       <span id="landtoken-terms">Términos y Condiciones</span>{" "}
                //       de Landtoken
                //     </div>
                //   ),
                //   links: [
                //     {
                //       id: "landtoken-terms",
                //       name: "Términos y Condiciones de Landtoken",
                //       url: `${staticUrl}/terms/Nota+SENEBI.pdf`,
                //     },
                //   ],
                // },
              ]
            : []),
          {
            required: false,
            name: "senebiAndExteriorNotes",
            label: (
              <div>
                Acepto la <span id="senebi-note">Nota SENEBI</span> y{" "}
                <span id="exterior-note">Nota exterior</span>
              </div>
            ),
            links: [
              {
                id: "senebi-note",
                name: "Nota SENEBI",
                url: `${staticUrl}/terms/Nota+SENEBI.pdf`,
              },
              {
                id: "exterior-note",
                name: "Nota exterior",
                url: `${staticUrl}/terms/Nota+de+operaciones+en+el+exterior.pdf`,
              },
            ],
          },
        ],
        phone: "5491140411111",
        mail: "atencionalcliente@allaria.com.ar",
      };
    case "AGROFINANZAS":
      return {
        formTitle: "Onboarding Agro Finanzas",
        logo: logoAgroFinanzas,
        logoClassname: "agroFinanzasLogo",
        favicon: faviconAllariaMas,
        companyName: company,
        termsAndConditions: [
          {
            required: true,
            name: "termsAndConditions",
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones Agrofinanzas",
                url: `${staticUrl}/terms/Convenio+y+mandato+AAF.pdf`,
              },
            ],
          },
          {
            required: false,
            name: "wantsAllariaAccount",
            label: (
              <div>
                Quiero una comitente Allaria. Ver{" "}
                <span id="allaria-account">Términos y Condiciones</span>
              </div>
            ),
            links: [
              {
                id: "allaria-account",
                name: "Términos y Condiciones de Allaria",
                url: `${staticUrl}/terms/T%26C+Allaria+con+A%2B.pdf`,
              },
            ],
          },
          {
            required: false,
            name: "senebiAndExteriorNotes",
            label: (
              <div>
                Acepto la <span id="senebi-note">Nota SENEBI</span> y{" "}
                <span id="exterior-note">Nota exterior</span>
              </div>
            ),
            links: [
              {
                id: "senebi-note",
                name: "Nota SENEBI",
                url: `${staticUrl}/terms/11.Instrucción+op+SENEBI+-Mayo+23.pdf`,
              },
              {
                id: "exterior-note",
                name: "Nota exterior",
                url: `${staticUrl}/terms/10.Instrucción+op+Exterior+-Mayo+23.pdf`,
              },
            ],
          },
        ],
        phone: "541167678324",
        mail: "compliance@allariaagro.com.ar",
      };
    case "ALLARIA_SECURITY":
      return {
        formTitle: "Onboarding Allaria Securities LLC",
        logo: logoAllariaSecurities,
        logoClassname: "securitiesLogo",
        favicon: faviconAllaria,
        companyName: company,
        termsAndConditions: [
          {
            name: "termsAndConditions",
            required: true,
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones de Allaria",
                url: `${staticUrl}/terms/Allaria%20Términos%20y%20Condiciones.pdf`,
              },
            ],
          },
          {
            required: false,
            name: "senebiAndExteriorNotes",
            label: (
              <div>
                Acepto la <span id="senebi-note">Nota SENEBI</span> y{" "}
                <span id="exterior-note">Nota exterior</span>
              </div>
            ),
            links: [
              {
                id: "senebi-note",
                name: "Nota SENEBI",
                url: `${staticUrl}/terms/Nota+SENEBI.pdf`,
              },
              {
                id: "exterior-note",
                name: "Nota exterior",
                url: `${staticUrl}/terms/Nota+de+operaciones+en+el+exterior.pdf`,
              },
            ],
          },
        ],
        phone: "5491140411111",
        mail: "atencionalcliente@allaria.com.ar",
      };
    case "ALLARIA_ASSET_MANAGEMENT":
      return {
        formTitle: "Onboarding Allaria Asset Management LLC",
        logo: logoAllariaAssetManagement,
        logoClassname: "assetManagementLogo",
        favicon: faviconAllaria,
        companyName: company,
        termsAndConditions: [
          {
            name: "termsAndConditions",
            required: true,
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones de Allaria",
                url: `${staticUrl}/terms/Allaria%20Términos%20y%20Condiciones.pdf`,
              },
            ],
          },
          {
            required: false,
            name: "senebiAndExteriorNotes",
            label: (
              <div>
                Acepto la <span id="senebi-note">Nota SENEBI</span> y{" "}
                <span id="exterior-note">Nota exterior</span>
              </div>
            ),
            links: [
              {
                id: "senebi-note",
                name: "Nota SENEBI",
                url: `${staticUrl}/terms/Nota+SENEBI.pdf`,
              },
              {
                id: "exterior-note",
                name: "Nota exterior",
                url: `${staticUrl}/terms/Nota+de+operaciones+en+el+exterior.pdf`,
              },
            ],
          },
        ],
        mail: "soporte@allariasecurities.com",
      };
    case "ALLARIAMAS":
    default:
      return {
        formTitle: "Onboarding Allaria+",
        logo: logoAllariaMas,
        favicon: faviconAllariaMas,
        companyName: company,
        termsAndConditions: [
          {
            required: true,
            name: "termsAndConditions",
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
                {" y "}
                {isUnifiedOnboarding() ? (
                  <>la unificación entre cuentas</>
                ) : (
                  <span id="platform-use">Uso de la plataforma</span>
                )}
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones de Allaria+",
                url: `${staticUrl}/terms/2)+TyC+Allaria%2B+Cuenta+%2B+Anexo+I+sin+aclaracion+(web+y+OB+Allaria%2B).pdf`,
              },
              {
                id: "platform-use",
                name: "Uso de la plataforma",
                url: `${staticUrl}/terms/Términos+y+condiciones+de+Uso+de+la+Plataforma.pdf`,
              },
            ],
          },
        ],
        phone: "5491138499171",
        mail: "soporte@allariamas.com.ar",
      };
  }
};

export const juridicalDocumentLinks = {
  "businessDocumentation.membersNote": `${staticUrl}/docs/4-+MODELO+ENTIDADES-AGRUPACIONES.docx`,
  "businessDocumentation.trustNote": `${staticUrl}/docs/2-+MODELO+FIDEICOMISO.docx`,
  "businessDocumentation.shareholdersNote": `${staticUrl}/docs/1-+MODELO+DDJJ+ACCIONISTAS+Y+BF.docx`,
  "businessDocumentation.partnersNote": `${staticUrl}/docs/3-+MODELO+SGR.doc`,
};

// Variables for support contact
export const PHONE_NUMBER = getCompanyData().phone;
export const WHATSAPP_LINK = PHONE_NUMBER
  ? `https://wa.me/${PHONE_NUMBER}`
  : undefined;
export const MAIL = getCompanyData().mail;
export const MAIL_BLACKLIST = `aperturas.soporte@allaria.com.ar`;

export const customToastifyError = (message: string) => {
  return <div>{message}</div>;
};

// Styles for QR Image
export const qrStyle = {
  color: {
    dark: "#224288",
  },
  margin: 0,
  width: 200,
};

export const validDniTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/heic",
  "image/heif",
  "application/pdf",
];

export const validBusinessDocumentationTypes = [
  "application/msword", //.doc
  "application/pdf",
  "image/jpeg",
  "image/png",
];

export const maxFileSize = 10; // MB
export const minFileSize = 0.005; // MB (0.005 MB = 5 KB)

/**
 * Convert a string value to a CUIT format
 * @param {string} value - The value to be converted to CUIT format
 * @returns {string} - The value in CUIT format
 */
export const asCuit = (value: string) =>
  value.slice(0, 2) + "-" + value.slice(2, 10) + "-" + value.slice(10, 11);

export const asDni = (value: string) => {
  // Allow 7 or 8 digits
  let valueWithMask = value.replace(/^0/, "");

  if (value.length >= 2 && value.length <= 7) {
    valueWithMask = `${value.slice(0, 1)}.${value.slice(1, 4)}.${value.slice(
      4,
      7
    )}`;
  } else if (value.length >= 3 && value.length > 7) {
    valueWithMask = `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(
      5,
      8
    )}`;
  }

  if (valueWithMask.endsWith(".")) {
    return valueWithMask.slice(0, -1);
  } else {
    return valueWithMask;
  }
};

export const getDniFromCuit = (cuit: string) => cuit.slice(2, 10);

export const scrollToElement = (id: string) => {
  const element = document.getElementById(id);
  element && element.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const cities = [
  "Provincia de Buenos Aires",
  "CABA",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

export const cityOptions = () =>
  cities.map((city) => ({ value: city, label: city }));

//Backend not support "Ciudad Autónoma de Buenos Aires", so we parse it to "CABA"
export const parseStateAddress = (state: string) => {
  if (state === "Ciudad Autónoma de Buenos Aires") return "CABA";
  if (
    [
      "provincia de buenos aires",
      "buenos aires",
      "bs as",
      "bs.as.",
      "bs. as.",
    ].includes(state.toLowerCase())
  )
    return "Provincia de Buenos Aires";
  else return state;
};

export const hasJointSignature = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.businessInformation?.hasJointSignature === "SI";
};

export const isToros = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "Toros Capital";
};

export const isWina = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "Wina";
};

export const isLandToken = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "Landtoken";
};

export const isLaBragadense = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer?.toLowerCase() === "labragadense";
};
export const isMabra = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "MABRA";
};

export const isCompanyWithoutShareholders = (corporateType: string) =>
  [
    "Asociación / Cámara / Cooperativa / Fundación / Mutual",
    "Consorcio",
    "Fideicomiso",
    "Organismo y/o Entidad Gubernamental",
    "SGR - Sociedad de Garantía Recíproca",
  ].includes(corporateType);

export const isJuridicalCuit = (cuit: string) => {
  const juridicalPrefixes = ["30", "33"];
  return juridicalPrefixes.some((prefix) => cuit.startsWith(prefix));
};

// Helper function to convert booleans to "SI"/"NO" or keep null
export const convertBoolean = (value: boolean | null): string | null => {
  return value === null ? null : value ? "SI" : "NO";
};

export const convertStringToBoolean = (value: "SI" | "NO"): boolean =>
  value === "SI";

export const isUncompletedShareholder = (shareholder: ShareholderData) => {
  if (!shareholder) return true;

  const isMarried =
    shareholder?.personType === "PHYSICAL" &&
    shareholder.civilStatus === "MARRIED";

  if (isMarried && (!shareholder.spouseName || !shareholder.spouseLastName)) {
    return true;
  }
  return Object.entries(shareholder).some(([key, value]) => {
    if (!value) {
      if (isMarried) {
        // Si está casado, spouseName y spouseLastName también deben considerarse incompletos si están vacíos
        return key !== "pepDetails" && key !== "spouseCuil";
      } else {
        return (
          key !== "spouseName" &&
          key !== "spouseLastName" &&
          key !== "spouseCuil" &&
          key !== "pepDetails"
        );
      }
    }
    return false;
  });
};

export const isAllariaOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "ALLARIA";
};

export const isAgrofinanzasOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "AGROFINANZAS";
};

export const isAllariaMasOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "ALLARIAMAS";
};

export const minutesToMiliseconds = (minutes: number) => {
  return minutes * 60 * 1000;
};

// Mask email 3 last characters after the @
export const maskEmail = (email: string) => {
  const [user, domain] = email.split("@");
  return `${user.slice(0, -3)}***@${domain}`;
};

export const getCountries = async (): Promise<PitbullCountries> => {
  try {
    const { data } = await getCountryList();
    return data;
  } catch (error) {
    throw new Error("Error al obtener los países");
  }
};

export const yesOrNoBoolOptions = [
  { value: "SI", label: "Sí" },
  { value: "NO", label: "No" },
];

export const genderOptions = [
  { value: "M", label: "Masculino" },
  { value: "F", label: "Femenino" },
  { value: "X", label: "Otro" },
];

export const pepOptions = [
  { value: "DIRECT", label: "Sí, directamente" },
  { value: "FAMILY", label: "Sí, por vínculo familiar" },
  { value: "NONE", label: "No" },
];

export const civilStatusOptions: { value: CivilStatus; label: string }[] = [
  { value: "SINGLE", label: "Soltero/a" },
  { value: "MARRIED", label: "Casado/a" },
  { value: "WIDOWED", label: "Viudo/a" },
  { value: "DIVORCED", label: "Divorciado/a" },
];

export const employmentStatusOptions = [
  {
    value: "DEPENDENCY_RELATIONSHIP",
    label: "Trabajo en relación de dependencia",
  },
  { value: "MONOTAX", label: "Soy monotributista" },
  { value: "AUTONOMOUS", label: "Soy autónomo/a" },
  { value: "STUDENT", label: "Soy estudiante" },
  { value: "RETIRED", label: "Soy jubilado/a" },
  { value: "UNEMPLOYED", label: "No tengo empleo actualmente" },
];

export const employmentYearsOptions = [
  { value: "Menos de un año", label: "Menos de 1 año" },
  { value: "1 año", label: "1 año" },
  { value: "2 años", label: "2 años" },
  { value: "3 años", label: "3 años" },
  { value: "4 años", label: "4 años" },
  { value: "5 años", label: "5 años" },
  { value: "6 años", label: "6 años" },
  { value: "7 años", label: "7 años" },
  { value: "8 años", label: "8 años" },
  { value: "9 años", label: "9 años" },
  { value: "Más de 10 años", label: "10 años o más" },
];

export const documentTypesOptions = [
  { value: "DNI", label: "Documento Nacional de Identidad" },
  { value: "PASSPORT", label: "Pasaporte" },
];

export const validProofOfAddressTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
];

export const validDocumentImageTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "image/heic",
  "image/heif",
];

export const handleDateInputKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  value?: string
) => {
  // Allow: backspace, delete, tab, escape, enter, shift and backslash

  if (
    [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "Shift",
      "/",
    ].includes(e.key)
  ) {
    return true;
  }

  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
  if (
    (e.ctrlKey || e.metaKey) &&
    ["a", "c", "v", "x"].includes(e.key.toLowerCase())
  ) {
    return true;
  }

  // Only allow numbers
  if (!/^[0-9]$/.test(e.key)) {
    e.preventDefault();
    return false;
  }

  // Prevent more than 10 characters (DD/MM/YYYY format)
  if (value && value.toString().length >= 10) {
    e.preventDefault();
    return false;
  }

  return true;
};

export const handleDateFieldValueChanged = (
  newValue: string,
  setFieldValue: (value: string) => void
) => {
  const date = [...newValue.replace(/\//g, "").substring(0, 8)];
  [2, 4].forEach((at, i) => date.length > at && date.splice(at + i, 0, "/"));
  setFieldValue(date.join(""));
};

export const parseDateToSend = (date: string) => {
  /**
   *Parse date from DD/MM/YYYY to MM/DD/YYYY
   */
  const dateFormatted = moment(date, "DD/MM/YYYY").format("MM/DD/YYYY");
  return dateFormatted;
};

export const serializableFileAsFile = (
  serializableFile: SerializableFile | File
) =>
  serializableFile instanceof File
    ? serializableFile
    : new File([serializableFile.blob], serializableFile.name, {
        type: serializableFile.type,
      });

export const utilsForLanguage = (language: "es" | "en") => {
  // Generic content to show for toastify error messages
  const genericToastifyError = {
    es: (
      <div>
        Lo sentimos, algo falló inesperadamente. Intentalo nuevamente y si el
        error persiste, contactate con{" "}
        <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
          <b>Soporte.</b>
        </a>
      </div>
    ),
    en: (
      <div>
        Sorry, something failed unexpectadly. Try again and contact{" "}
        <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
          <b>Support</b>
        </a>{" "}
        if the issue persists.
      </div>
    ),
  }[language];

  return {
    genericToastifyError,
  };
};

export const preconfirmationPhone = (value: string) =>
  value ? (value.startsWith("+") ? value : `+${value}`) : "-";

/**
 * Decimal separator for numbers.
 * @param {string} [locale=es-AR] - Locale to interpret numbers as from.
 * @returns {string} - The decimal separator.
 */
export const decimalSeparatorForLocale = (locale = "es-AR") =>
  Number(1.1).toLocaleString(locale).charAt(1);

/**
 * Format a value as currency.
 * @param {string | number} value - The value to be converted to formatted as currency.
 * @param {string} [locale=es-AR] - Locale to interpret numbers as from.
 * @param {boolean} [alwaysShowDecimalPart=false] - Append ",00" to values without decimal parts.
 * @returns {string} - The value formatted as currency.
 */
export const formatAsCurrency = (
  value: string | number,
  locale = "es-AR",
  alwaysShowDecimalPart = false
) => {
  const [whole, decimal = "00"] = value.toLocaleString(locale).split(".", 2);

  const showDecimal = (!!decimal && decimal !== "00") || alwaysShowDecimalPart;

  if (!whole) return `$${value}`;
  const wholeNumber = Number.parseInt(whole);
  return isNaN(wholeNumber)
    ? value
    : `$${
        wholeNumber.toLocaleString(locale) +
        (showDecimal ? `${decimalSeparatorForLocale(locale)}${decimal}` : "")
      }`;
};

export const firestoreCollectionByDocumentType: Record<DocumentType, string> = {
  DNI_BACK: "dniImages",
  DNI_FRONT: "dniImages",
  PASSPORT_FIRST_PAGE: "passportImages",
  PASSPORT_SECOND_PAGE: "passportImages",
  PROOF_OF_ADDRESS: "proofOfAddressImages",
  SELFIE: "",
  WORTH_STATEMENT: "",
};

export const firestoreDocumentByDocumentType: Record<DocumentType, string> = {
  DNI_BACK: "frontDNI",
  DNI_FRONT: "backDNI",
  PASSPORT_FIRST_PAGE: "firstPagePassport",
  PASSPORT_SECOND_PAGE: "secondPagePassport",
  PROOF_OF_ADDRESS: "proofOfAddress",
  SELFIE: "",
  WORTH_STATEMENT: "",
};

export const mimeForExtension: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".pdf": "application/pdf",
  ".heic": "image/heic",
  ".heif": "image/heif",
};

export const emptyNamedSerializableFile = (name: string) => ({
  name: name,
  // HACK: It doesn't really matter, but trying to infer type is cleaner than
  // adding a `""` type to the inputs' valid types
  type:
    mimeForExtension?.[name?.split(".").pop()?.toLowerCase() ?? ""] ??
    "image/jpeg",
  lastModified: 0,
  size: 0,
  blob: new ArrayBuffer(0),
});

export const isEmptyFile = (file: File | SerializableFile) => file.size === 0;

export const { genericToastifyError } = utilsForLanguage("es");

export const getUnificationParams = () => {
  const params = new URLSearchParams(window.location.search);
  const accountId = params.get("accountId") || "";
  const accountName = params.get("accountName") || "";
  const cuit = params.get("accountCuit") || "";
  const email = params.get("accountEmail") || "";

  return { accountId, accountName, cuit, email };
};
export const isPhysicalOnboarding = (onboardingType: OnboardingType) => {
  return onboardingType === "PHYSICAL";
};

export const isJuridicalOnboarding = (onboardingType: OnboardingType) => {
  return onboardingType === "JURIDICAL";
};

export const isSignatoryOnboarding = (onboardingType: OnboardingType) => {
  return onboardingType === "SIGNATORY";
};

export const getPartnerCompanyType = (
  formValues: FormikValues
): PartnerCompanyType | null => {
  if (isLandToken(formValues)) {
    return "LANDTOKEN";
  } else if (isLaBragadense(formValues)) {
    return "LA_BRAGADENSE";
  }

  return null;
};

export const loadFavicon = (favicon: string) => {
  const newLink = document.createElement("link");
  newLink.rel = "icon";
  newLink.type = "image/png";
  newLink.href = favicon;
  document.head.appendChild(newLink);
};

/**
 * Verifica si un usuario está en la blacklist basado en su email y CUIT
 * @param email - Email del usuario
 * @param cuit - CUIT del usuario
 * @returns Promise<boolean> - true si está en blacklist, false si no
 */
export const checkUserInBlacklist = async (
  email: string,
  cuit: string
): Promise<boolean> => {
  try {
    const db = getFirestore(app);
    const blacklistCollection = isDev ? "blacklistDev" : "blacklistProd";
    const blacklistCollectionRef = collection(db, blacklistCollection);

    const querySnapshot = await getDocs(blacklistCollectionRef);

    const blacklistItems: Array<{
      id: string;
      cuit: string;
      email: string;
      company: string;
      [key: string]: unknown;
    }> = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      blacklistItems.push({
        id: doc.id,
        cuit: data.cuit,
        email: data.email,
        company: data.company,
        ...data,
      });
    });

    const isInBlacklist = blacklistItems.some((item) => {
      const emailMatch =
        email && item.email && item.email.toLowerCase() === email.toLowerCase();
      const cuitMatch = cuit && item.cuit && item.cuit === cuit;

      return emailMatch || cuitMatch;
    });

    return isInBlacklist;
  } catch (error) {
    console.error("Error verificando blacklist:", error);
    // En caso de error, no bloquear al usuario
    return false;
  }
};

export const checkIsEmailAvailable = async ({
  dispatch,
  idNumber,
}: {
  dispatch: AppDispatch;
  idNumber: string;
}) => {
  try {
    const { data } = await checkAlreadyRegistered({
      idNumber,
    });
    if (data.state === "IN_USE") {
      dispatch(onUpdateInfoStep("REGISTERED_EMAIL"));
      dispatch(onUpdateStep("information-step"));
    }
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const getInfoStepData = (
  infoStep: InfoStepType,
  onboarding: OnboardingType
) => {
  let title = "";
  let subtitle: string | React.ReactNode = "";
  let primaryText = "";
  let secondaryText = "";
  let alertType: "INFO" | "ERROR" = "INFO";

  switch (infoStep) {
    case "PENDING_REGISTRY_LOCAL":
    case "PENDING_REGISTRY_REMOTE":
      if (onboarding === "SIGNATORY" && infoStep === "PENDING_REGISTRY_REMOTE")
        break;
      title = "Tenés un registro sin terminar";
      subtitle = "¿Deseás retomar desde donde lo dejaste?";
      primaryText = "Retomar registro";
      secondaryText = "Empezar de nuevo";
      break;
    case "ACTIVE_ACCOUNT":
      title = "¡Un proceso veloz!";
      subtitle = "Agilizamos la apertura precargando tus datos.";
      primaryText = "Continuar";
      break;
    case "REGISTERED_EMAIL":
      title = "Correo electrónico ya registrado";
      subtitle =
        onboarding === "SIGNATORY"
          ? "El mail ingresado está asociado a otro número de documento. Por favor, verificá el CUIT/CUIL ingresado."
          : "Por favor, verificá el DNI asociado para continuar.";
      primaryText =
        onboarding === "SIGNATORY" ? "Modificar" : "Modificar datos";
      break;
    case "BLACKLIST":
      alertType = "ERROR";
      title = "Apertura no disponible";
      subtitle = (
        <span>
          Tu solicitud de apertura de cuenta comitente no puede completarse por
          este canal. Si creés que es un error o tenés dudas, escribinos a{" "}
          <a href={`mailto:${MAIL_BLACKLIST}`}>{MAIL_BLACKLIST}</a>
        </span>
      );
      primaryText = "Contactar soporte";
      secondaryText = "Volver";
      break;
    case "DRAFT_NOT_FOUND":
      title = "No pudimos retomar tu registro";
      subtitle =
        "El proceso de registro que habías comenzado ya no está disponible. Iniciá un nuevo registro para continuar.";
      primaryText = "Empezar nuevamente";
      break;
    //Error CUIT
    case null:
      if (onboarding === "JURIDICAL") {
        title = "El CUIT corresponde a una persona física";
        subtitle = "¿Deseás continuar y registrar una cuenta personal?";
      } else if (onboarding === "PHYSICAL") {
        title = "El CUIT corresponde a una persona jurídica";
        subtitle = "¿Deseás continuar y registrar una cuenta empresarial?";
      }
      primaryText = "Continuar";
      secondaryText = "Volver";
      break;
    default:
      break;
  }

  return {
    alertType,
    title,
    subtitle,
    primaryText,
    secondaryText,
  };
};

export const deleteDniImages = async (db: Firestore, draftUUID: DraftUUID) => {
  const docRef = doc(db, "dniImages", draftUUID);
  await deleteDoc(docRef);
};

export const deleteLiveness = async (db: Firestore, draftUUID: DraftUUID) => {
  const docRef = doc(db, "livenessValidation", draftUUID);
  await deleteDoc(docRef);
};

export const contactBlacklistSupport = () => {
  const link = document.createElement("a");
  link.href = `mailto:${MAIL_BLACKLIST}`;
  link.click();
};
